import { useState, useEffect, useCallback, useRef } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
  buildTimestamp: number;
  commitHash: string;
  buildEnv: string;
  salt: string;
}

interface UseVersionCheckOptions {
  /** Check interval in milliseconds (default: 30 seconds) */
  checkInterval?: number;
  /** Enable automatic refresh on version change (default: false) */
  autoRefresh?: boolean;
  /** Show console logs for debugging (default: false) */
  debug?: boolean;
}

interface UseVersionCheckReturn {
  /** Current version information */
  currentVersion: VersionInfo | null;
  /** Whether a new version is available */
  hasNewVersion: boolean;
  /** Whether currently checking for updates */
  isChecking: boolean;
  /** Last check timestamp */
  lastChecked: Date | null;
  /** Manually trigger version check */
  checkForUpdate: () => Promise<void>;
  /** Refresh the application */
  refreshApp: () => void;
  /** Error if version check failed */
  error: string | null;
}

const VERSION_STORAGE_KEY = 'memara_app_version';
const MIN_CHECK_INTERVAL = 10000; // Minimum 10 seconds between checks to prevent spam

export function useVersionCheck(options: UseVersionCheckOptions = {}): UseVersionCheckReturn {
  const {
    checkInterval = import.meta.env.DEV ? 300000 : 60000, // 5 minutes in dev, 1 minute in prod
    autoRefresh = false,
    debug = false
  } = options;

  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to track checking state without causing callback recreation
  const isCheckingRef = useRef(false);
  const lastCheckTimeRef = useRef<number>(0);

  const log = useCallback((message: string, ...args: any[]) => {
    if (debug) {
      console.log(`[VersionCheck] ${message}`, ...args);
    }
  }, [debug]);

  const getStoredVersion = useCallback((): VersionInfo | null => {
    try {
      const stored = localStorage.getItem(VERSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      log('Error reading stored version:', error);
      return null;
    }
  }, [log]);

  const storeVersion = useCallback((version: VersionInfo) => {
    try {
      localStorage.setItem(VERSION_STORAGE_KEY, JSON.stringify(version));
    } catch (error) {
      log('Error storing version:', error);
    }
  }, [log]);

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      // Add cache busting parameter
      const cacheBuster = `?t=${Date.now()}`;
      const response = await fetch(`/version.json${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const version: VersionInfo = await response.json();
      log('Fetched version:', version);
      return version;
    } catch (error) {
      log('Error fetching version:', error);
      throw error;
    }
  }, [log]);

  const checkForUpdate = useCallback(async () => {
    const now = Date.now();
    
    // Prevent overlapping checks
    if (isCheckingRef.current) {
      log('Check already in progress, skipping...');
      return;
    }
    
    // Enforce minimum time between checks
    const timeSinceLastCheck = now - lastCheckTimeRef.current;
    if (timeSinceLastCheck < MIN_CHECK_INTERVAL) {
      log(`Skipping check - only ${timeSinceLastCheck}ms since last check (minimum: ${MIN_CHECK_INTERVAL}ms)`);
      return;
    }

    isCheckingRef.current = true;
    lastCheckTimeRef.current = now;
    setIsChecking(true);
    setError(null);

    try {
      const newVersion = await fetchVersion();
      if (!newVersion) {
        isCheckingRef.current = false;
        setIsChecking(false);
        return;
      }

      const storedVersion = getStoredVersion();
      setCurrentVersion(newVersion);
      setLastChecked(new Date());

      if (storedVersion) {
        // Compare build timestamps for accurate version comparison
        const isNewVersion = newVersion.buildTimestamp > storedVersion.buildTimestamp;
        
        if (isNewVersion) {
          log('New version detected!', {
            current: storedVersion,
            new: newVersion
          });
          
          setHasNewVersion(true);
          
          if (autoRefresh) {
            log('Auto-refreshing application...');
            setTimeout(() => refreshApp(), 1000);
          }
        } else {
          setHasNewVersion(false);
        }
      }

      // Update stored version
      storeVersion(newVersion);
    } catch (error: any) {
      setError(error.message);
      log('Version check failed:', error);
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, [fetchVersion, getStoredVersion, storeVersion, autoRefresh, log]);

  const refreshApp = useCallback(() => {
    log('Refreshing application...');
    window.location.reload();
  }, [log]);

  // Initial version check
  useEffect(() => {
    checkForUpdate();
  }, [checkForUpdate]);

  // Periodic version checks
  useEffect(() => {
    const interval = setInterval(checkForUpdate, checkInterval);
    return () => clearInterval(interval);
  }, [checkForUpdate, checkInterval]);

  // Check for version on page visibility change (user returns to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        log('Page became visible, checking for updates...');
        checkForUpdate();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkForUpdate, log]);

  return {
    currentVersion,
    hasNewVersion,
    isChecking,
    lastChecked,
    checkForUpdate,
    refreshApp,
    error
  };
}
